import { useEffect, useState, RefObject } from 'react';

/** Stable signature so object/array deps from parents do not change identity every render. */
function serializeDependencies(deps: unknown[]): string {
  try {
    return JSON.stringify(deps);
  } catch {
    return deps.map(d => String(d)).join('|');
  }
}

/**
 * Hook to track when all images within a container element are loaded.
 *
 * @param containerRef - Ref to the container element that contains the images
 * @param dependencies - Optional array of dependencies that should reset the loading state when changed
 * @returns boolean indicating if all images are loaded
 */
export function useImageLoadTracker(containerRef: RefObject<HTMLElement>, dependencies: unknown[] = []): boolean {
  const [areImagesLoaded, setAreImagesLoaded] = useState(false);
  // Serialize so AEM/config objects that are new references each render do not retrigger the effect.
  const dependenciesKey = serializeDependencies(dependencies);

  useEffect(() => {
    setAreImagesLoaded(false);

    if (!containerRef.current) return;

    let cleanup: (() => void) | undefined;

    // Small delay to ensure images are in the DOM, especially when content changes
    const ensureImagesAreInDOM = setTimeout(() => {
      if (!containerRef.current) return;

      const imgElements = containerRef.current.querySelectorAll('img');

      if (imgElements.length === 0) {
        setAreImagesLoaded(true);
        return;
      }

      let loadedCount = 0;
      const checkImageLoaded = (img: HTMLImageElement) => {
        if (img.complete && img.naturalWidth > 0) {
          loadedCount++;
          if (loadedCount === imgElements.length) {
            setAreImagesLoaded(true);
          }
        }
      };

      const handleLoad = () => {
        loadedCount++;
        if (loadedCount === imgElements.length) {
          setAreImagesLoaded(true);
        }
      };

      imgElements.forEach(img => {
        checkImageLoaded(img);
        if (!(img.complete && img.naturalWidth > 0)) {
          img.addEventListener('load', handleLoad, { once: true });
          img.addEventListener('error', handleLoad, { once: true });
        }
      });

      cleanup = () => {
        imgElements.forEach(img => {
          img.removeEventListener('load', handleLoad);
          img.removeEventListener('error', handleLoad);
        });
      };
    }, 0);

    return () => {
      clearTimeout(ensureImagesAreInDOM);
      cleanup?.();
    };
  }, [containerRef, dependenciesKey]);

  return areImagesLoaded;
}
